import { Component, computed, DestroyRef, effect, inject, signal } from '@angular/core';
import { take } from 'rxjs';
import { isAfter } from 'date-fns';
import { LessonDetailStateService } from '../lesson-detail-state.service';
import { LessonCardV2Component } from '../../lesson-card-v2/lesson-card-v2.component';
import { LessonDetailDailyGradesComponent } from '../lesson-detail-daily-grades/lesson-detail-daily-grades.component';
import { LessonDetailObjectivesComponent } from '../lesson-detail-objectives/lesson-detail-objectives.component';
import { LessonDetailProjectProgressComponent } from '../lesson-detail-project-progress/lesson-detail-project-progress.component';
import { LessonDetailHomeworkComponent } from '../lesson-detail-homework/lesson-detail-homework.component';
import { LessonDetailSummaryComponent } from '../lesson-detail-summary/lesson-detail-summary.component';
import { LessonDetailProjectFinalComponent } from '../lesson-detail-project-final/lesson-detail-project-final.component';
import { LessonDetailSurveyComponent } from '../lesson-detail-surveys/lesson-detail-survey.component';
import { LessonDetailUnavailableComponent, LessonDetailUnavailableReason } from '../lesson-detail-unavailable/lesson-detail-unavailable.component';
import { RescheduleLessonDialogService } from '@lb/services/reschedule-lesson-dialog.service';
import { canRescheduleLesson } from '@lb/components/lessons/utils/lesson-helper.util';
import { NotificationsStateService } from '@lb/state/notifications-state.service';
import { NotificationType } from '@lb/api/mylb/enums';
import { AnalyticsEvent, AnalyticsService } from '@lb/services';

@Component({
	selector: 'lb-learner-lesson-detail',
	templateUrl: './learner-lesson-detail.component.html',
	styleUrl: './learner-lesson-detail.component.scss',
	imports: [
		LessonCardV2Component,
		LessonDetailDailyGradesComponent,
		LessonDetailObjectivesComponent,
		LessonDetailProjectProgressComponent,
		LessonDetailHomeworkComponent,
		LessonDetailSummaryComponent,
		LessonDetailSurveyComponent,
		LessonDetailProjectFinalComponent,
		LessonDetailUnavailableComponent
	],
	standalone: true
})
export class LearnerLessonDetailComponent {
	readonly state = inject(LessonDetailStateService);
	private readonly _rescheduleLessonDialogService = inject(RescheduleLessonDialogService);
	private readonly _destroyRef = inject(DestroyRef);
	private readonly _now = signal(new Date());
	private readonly notificationStateService = inject(NotificationsStateService);
	private readonly _analytics = inject(AnalyticsService);

	lesson = this.state.lessonRequired;
	isInstructional = this.state.isInstructional;
	isFinalSemester = this.state.isFinalSemester;
	hasGradingTemplate = this.state.hasGradingTemplate;
	isAbsent = this.state.isAbsent;

	pendingReschedule = computed(() => this.lesson().lessonStatusID === 3);
	awaitingPayment = computed(() => this.lesson().lessonStatusID === 4);
	isComplete = computed(() => this.lesson().lessonStatusID === 5);

	canJoin = computed(() => !this.pendingReschedule() && !this.awaitingPayment() && !this.isComplete());
	canReschedule = computed(() => canRescheduleLesson(this.state.experience(), this.lesson(), this._now()));

	// Instructional lessons only get the full graded layout when a grading template is actually
	// assigned to the program AND that template is graded (chirp room templates are not). Without
	// either, they fall back to the same summary-only view as any other non-graded lesson type.
	isGradedInstructional = computed(() => this.isInstructional() && this.hasGradingTemplate() && this.lesson().isGraded);

	/**
	 * Whether the instructor's write-up is the learner's to read yet. Instructors can now pre-populate
	 * a lesson before teaching it and save it as a draft, so "details exist" no longer means "details
	 * are final" — the learner waits for the lesson to actually be taught (attendance recorded, or the
	 * lesson completed), which is also the moment the "grades available" notification fires.
	 *
	 * Surveys are deliberately not gated on this: they have their own availability window and are
	 * meant to be answerable before the lesson.
	 */
	lessonWorkVisible = computed(() => this.lesson().attendanceStatusID != null || this.isComplete());

	/**
	 * Whether the left-hand column has anything to put in it. It carries the survey and — only once
	 * the lesson's work is the learner's to read — the graded blocks, so a lesson that hasn't been
	 * taught and has no survey leaves it empty. Without this the column opposite still claims half
	 * the row and the summary (or its placeholder) renders beside nothing.
	 */
	sideContentVisible = computed(() => {
		if (this.state.hasSurvey() || this.state.externalFeedbackSurveyUrl()) {
			return true;
		}

		return this.lessonWorkVisible() && (this.isFinalSemester() || this.isGradedInstructional());
	});

	/**
	 * Why the write-up isn't here yet. A lesson whose time has already passed is not "not yet taught"
	 * — the learner sat through it and is waiting on the instructor — so it gets its own copy. _now
	 * ticks every minute, so the message flips on its own if the page is left open across the end time.
	 */
	detailsUnavailableReason = computed<LessonDetailUnavailableReason>(() => {
		const lesson = this.lesson();
		const lessonEnd = new Date(lesson.lessonEndDate || lesson.lessonDate);

		return isAfter(this._now(), lessonEnd) ? 'awaiting-details' : 'not-yet-taught';
	});

	// Server-computed over the whole enrollment (ILessonListItem.lessonNumber).
	lessonNumber = computed(() => this.lesson().lessonNumber ?? null);

	// Tracks the lesson we've already dismissed the "grades available" prompt for, so the effect below
	// dismisses at most once per lesson (it re-runs whenever the notifications feed changes).
	private _dismissedForLessonID: number | null = null;

	constructor() {
		const interval = setInterval(() => this._now.set(new Date()), 60_000);
		this._destroyRef.onDestroy(() => clearInterval(interval));

		// Ensure the feed loads even if the user lands here before it has been fetched (no-op if already loaded).
		this.notificationStateService.loadNotifications();

		// Dismiss the "grades available" notification for this lesson once the feed is loaded. Using an
		// effect (instead of a one-shot ngOnInit check) makes dismissal reliable regardless of whether the
		// feed finished loading before or after this page initialized.
		effect(() => {
			if (!this.notificationStateService.loaded()) {
				return;
			}

			const lessonID = this.state.lessonID();
			if (this._dismissedForLessonID === lessonID) {
				return;
			}

			const lessonNotification = this.notificationStateService
				.notifications()
				.find((n) => n.entityID === lessonID && n.notificationType === NotificationType.LessonGradesAvailable);

			if (lessonNotification) {
				this._dismissedForLessonID = lessonID;
				this.notificationStateService.dismissNotifications(lessonNotification.notificationID);
			}
		});
	}

	onJoinLesson(): void {
		this._analytics.trackEvent(AnalyticsEvent.LessonJoined, this._lessonContext());
	}

	reschedule(): void {
		this._rescheduleLessonDialogService
			.open(this.lesson())
			.pipe(take(1))
			.subscribe((result) => {
				if (result) {
					this._analytics.trackEvent(AnalyticsEvent.LessonRescheduled, this._lessonContext());
					this.state.reloadAfterChange(this.lesson().lessonID);
				}
			});
	}

	private _lessonContext(): Record<string, unknown> {
		const lesson = this.lesson();
		return { lessonId: lesson.lessonID, enrollmentId: lesson.enrollmentID };
	}
}
